import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, getFormByUrl, submitFormData } from "../../lib/getForms";
import { metaPixelCatalogService } from "../../services/metaPixelCatalogService";
import styles from "./Forms.module.css";

interface FormValues {
  [key: string]: string | number | boolean | File | null;
}

export const FormRenderer: React.FC = () => {
  const { formUrl } = useParams<{ formUrl: string }>();
  const [form, setForm] = useState<Form | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Fetch form data when component mounts
  useEffect(() => {
    const fetchForm = async () => {
      if (!formUrl) return;

      setIsLoading(true);
      setError(null);

      try {
        console.log(`Fetching form with URL: ${formUrl}`);
        const formData = await getFormByUrl(formUrl);
        console.log("Form data result:", formData);

        if (formData) {
          setForm(formData);

          // Initialize form values with default values
          const initialValues: FormValues = {};
          formData.sections.forEach((section) => {
            section.fields.forEach((field) => {
              if (field.defaultValue !== undefined) {
                initialValues[field.slug] = field.defaultValue;
              } else if (field.type === "checkbox") {
                initialValues[field.slug] = false;
              } else {
                initialValues[field.slug] = "";
              }
            });
          });
          setFormValues(initialValues);
        } else {
          console.error("Form data is null");
          setError("Form not found");
        }
      } catch (err) {
        console.error("Form fetch error:", err);
        setError("Failed to load form");
      } finally {
        setIsLoading(false);
      }
    };

    fetchForm();
  }, [formUrl]);

  // Handle form field changes
  const handleChange = (
    field: string,
    value: string | number | boolean | File | null
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // For select/dropdown fields, keep all other field values
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle file input changes
  const handleFileChange = (
    field: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0] || null;
    handleChange(field, file);
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!form) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Process form values before submission
      const processedData = { ...formValues };

      // Convert File objects to their names (actual file upload would require FormData)
      Object.keys(processedData).forEach((key) => {
        if (processedData[key] instanceof File) {
          // For real file uploads, you'd use FormData and handle it separately
          processedData[key] = (processedData[key] as File).name;
        }
      });

      const result = await submitFormData(form.id, processedData);

      if (result.success) {
        // Track Meta Pixel form submission
        metaPixelCatalogService.trackFormSubmission(
          form.title,
          form.id.toString(),
          true,
          {
            form_url: formUrl,
            fields_count: Object.keys(processedData).length,
            form_type: "general_form",
          }
        );

        // Create a submission token for security
        const submissionToken = `token-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 15)}`;

        // Create a field labels map to preserve field labels for the success page
        const fieldLabelsMap: Record<string, string> = {};
        form.sections.forEach((section) => {
          section.fields.forEach((field) => {
            fieldLabelsMap[field.slug] = field.label;
          });
        });

        // Navigate to success page with form ID and submission ID using the new path
        navigate(`/formulario/exito/${form.id}/${result.id}`, {
          state: {
            formData: processedData,
            formTitle: form.title,
            submissionToken, // Include the token
            fieldLabels: fieldLabelsMap, // Include field labels
            successTitle: form.successTitle,
            successText: form.successText,
          },
        });
      } else {
        // Track failed form submission
        metaPixelCatalogService.trackFormSubmission(
          form.title,
          form.id.toString(),
          false,
          {
            form_url: formUrl,
            error: result.error || "Unknown error",
            form_type: "general_form",
          }
        );

        setError(result.error || "Failed to submit form");
      }
    } catch (err) {
      setError("An error occurred during submission");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get CSS class based on field width
  const getWidthClass = (width: string): string => {
    switch (width) {
      case "w1/4":
        return styles.width25;
      case "w1/3":
        return styles.width33;
      case "w1/2":
        return styles.width50;
      case "w2/3":
        return styles.width66;
      case "full":
      default:
        return styles.widthFull;
    }
  };

  // Render a form field based on its type
  const renderField = (field: any, sectionId: number) => {
    const widthClass = getWidthClass(field.width);

    // Check if fullRow property exists and is true
    const isFullRow = field.fullRow === true;

    // Keep original width class, don't replace it with fullRowField
    const layoutClass = widthClass;

    console.log(
      `Field: ${field.label}, fullRow: ${isFullRow}, width: ${field.width}, class: ${layoutClass}`
    );

    // Create a unique ID for the field
    const fieldId = `field-${sectionId}-${field.id}`;

    return (
      <>
        <div key={fieldId} className={`${styles.formField} ${layoutClass}`}>
          <label htmlFor={fieldId} className={styles.fieldLabel}>
            {field.label}
            {field.isRequired && <span className={styles.requiredMark}>*</span>}
          </label>

          {field.helpText && (
            <div className={styles.helpText}>{field.helpText}</div>
          )}

          {renderFieldInput(field, fieldId)}
        </div>

        {/* Add an empty spacer div after fields that should take a full row */}
        {isFullRow && (
          <div className={styles.fullRowSpacer} aria-hidden="true"></div>
        )}
      </>
    );
  };

  // Render the appropriate input element for a field
  const renderFieldInput = (field: any, fieldId: string) => {
    // Debug options format
    if (field.type === "select" || field.type === "dropdown") {
      console.log(`Options for field ${field.label}:`, {
        rawOptions: field.options,
        type: typeof field.options,
        isArray: Array.isArray(field.options),
        hasChoices: field.options?.choices !== undefined,
      });
    }

    switch (field.type) {
      case "text":
      case "email":
      case "number":
        return (
          <input
            id={fieldId}
            type={field.type}
            name={field.slug}
            placeholder={field.placeholder || ""}
            value={(formValues[field.slug] as string) || ""}
            onChange={(e) => handleChange(field.slug, e.target.value)}
            required={field.isRequired}
            className={styles.textInput}
          />
        );

      case "textarea":
        return (
          <textarea
            id={fieldId}
            name={field.slug}
            placeholder={field.placeholder || ""}
            value={(formValues[field.slug] as string) || ""}
            onChange={(e) => handleChange(field.slug, e.target.value)}
            required={field.isRequired}
            className={styles.textareaInput}
            rows={5}
          />
        );

      case "select":
      case "dropdown":
        // Parse options if they're stored as a JSON string
        let optionsArray = [];
        try {
          if (typeof field.options === "string") {
            optionsArray = JSON.parse(field.options);
          } else if (Array.isArray(field.options)) {
            optionsArray = field.options;
          } else if (
            field.options?.choices &&
            Array.isArray(field.options.choices)
          ) {
            optionsArray = field.options.choices;
          }
        } catch (e) {
          console.error("Error parsing options:", e, field.options);
        }

        return (
          <select
            id={fieldId}
            name={field.slug}
            value={(formValues[field.slug] as string) || ""}
            onChange={handleSelectChange} // Use the special handler for selects
            required={field.isRequired}
            className={styles.selectInput}
          >
            <option value="">-- Por favor, seleccionar --</option>
            {optionsArray.map((option: any, index: number) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <div className={styles.checkboxContainer}>
            <input
              id={fieldId}
              type="checkbox"
              name={field.slug}
              checked={!!formValues[field.slug]}
              onChange={(e) => handleChange(field.slug, e.target.checked)}
              className={styles.checkboxInput}
            />
            <span className={styles.checkboxLabel}>
              {field.placeholder || ""}
            </span>
          </div>
        );

      case "radio":
        return (
          <div className={styles.radioGroup}>
            {field.options?.choices?.map((option: any, index: number) => (
              <div key={index} className={styles.radioItem}>
                <input
                  id={`${fieldId}-${index}`}
                  type="radio"
                  name={field.slug}
                  value={option.value}
                  checked={formValues[field.slug] === option.value}
                  onChange={(e) => handleChange(field.slug, e.target.value)}
                  required={field.isRequired}
                  className={styles.radioInput}
                />
                <label
                  htmlFor={`${fieldId}-${index}`}
                  className={styles.radioLabel}
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case "file":
        return (
          <input
            id={fieldId}
            type="file"
            name={field.slug}
            onChange={(e) => handleFileChange(field.slug, e)}
            required={field.isRequired}
            className={styles.fileInput}
          />
        );

      default:
        return <div>Unsupported field type: {field.type}</div>;
    }
  };

  if (isLoading) {
    return <div className={styles.loading}>Cargando formulario...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!form) {
    return <div className={styles.error}>Formulario no encontrado</div>;
  }

  return (
    <div className={styles.formContainer}>
      <h1 className={styles.formTitle}>{form.title}</h1>

      {form.description && (
        <div className={styles.formDescription}>{form.description}</div>
      )}

      {/* Add this debug output temporarily */}
      <div
        style={{
          background: "#f9f9f9",
          padding: "10px",
          margin: "10px 0",
          borderRadius: "4px",
          fontSize: "12px",
          display: "none", // Set to 'block' to display
        }}
      >
        <strong>Debug de datos del formulario:</strong>
        <pre>{JSON.stringify(form, null, 2)}</pre>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        {form.sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <div key={section.id} className={styles.formSection}>
              {section.title && (
                <h2 className={styles.sectionTitle}>{section.title}</h2>
              )}

              {section.description && (
                <div
                  className={styles.sectionDescription}
                  dangerouslySetInnerHTML={{
                    __html: section.description || "",
                  }}
                />
              )}

              <div className={styles.fieldsGrid}>
                {section.fields
                  .sort((a, b) => a.order - b.order)
                  .map((field) => renderField(field, section.id))}
              </div>
            </div>
          ))}

        <div className={styles.formActions}>
          <button type="submit" disabled={isSubmitting} className="buttonMain">
            {isSubmitting ? "Enviando..." : form.submitButtonLabel || "Enviar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormRenderer;
